import React, { useState, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { UtensilsCrossed, Camera, Loader2, AlertCircle, Check, X } from 'lucide-react';
import { FoodThumbnail } from '@/components/FoodThumbnail';
import axios from 'axios';

const API = '/api';

export const MenuScanner = ({ onItemSelect, mealType }) => {
  const { token, authHeaders } = useAuth();
  const fileInputRef = useRef(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [error, setError] = useState('');

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleScan = async () => {
    if (!imageFile) return;
    setScanning(true);
    setError('');
    const formData = new FormData();
    formData.append('file', imageFile);
    try {
      const res = await axios.post(`${API}/meals/scan-menu`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` },
        
      });
      if (res.data.error) {
        setError(res.data.error);
      } else {
        setMenuItems(res.data.items || []);
      }
    } catch (err) {
      setError('Failed to scan menu. Please try again.');
    }
    setScanning(false);
  };

  const toggleItem = (idx) => {
    setSelectedItems(prev =>
      prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]
    );
  };

  const handleConfirmSelection = () => {
    const items = selectedItems.map(idx => menuItems[idx]).map(item => ({
      name: item.name,
      quantity: '1 serving',
      calories: item.calories,
      protein: item.protein,
      carbs: item.carbs,
      fat: item.fat,
    }));
    onItemSelect(items);
  };

  // Results view
  if (menuItems.length > 0) {
    return (
      <div className="animate-slide-up-spring" data-testid="menu-results">
        {imagePreview && (
          <img src={imagePreview} alt="Menu" className="w-full aspect-[3/2] object-cover rounded-2xl mb-4 opacity-60" />
        )}
        <h3 className="text-base font-semibold text-[#1A3A4A] dark:text-[#F0EDE8] mb-3">
          Select items you ordered ({selectedItems.length} selected)
        </h3>
        <div className="space-y-2 mb-4 max-h-[50vh] overflow-y-auto">
          {menuItems.map((item, idx) => (
            <button
              key={idx}
              data-testid={`menu-item-${idx}`}
              onClick={() => toggleItem(idx)}
              className={`w-full p-4 rounded-xl text-left transition-all border-2 ${
                selectedItems.includes(idx)
                  ? 'border-[#6B9E8A] bg-[#6B9E8A]/5'
                  : 'border-transparent bg-[#FAF8F5] dark:bg-[#141C22] hover:border-[#1A3A4A]/10'
              }`}
            >
              <div className="flex items-start gap-3">
                <FoodThumbnail foodName={item.name} size="sm" className="rounded-lg mt-0.5" />
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                  selectedItems.includes(idx) ? 'bg-[#6B9E8A] border-[#6B9E8A]' : 'border-[#7A7A72]/30'
                }`}>
                  {selectedItems.includes(idx) && <Check className="w-3 h-3 text-white" />}
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-sm text-[#1A3A4A] dark:text-[#F0EDE8]">{item.name}</h4>
                  {item.description && <p className="text-xs text-[#7A7A72] mt-0.5">{item.description}</p>}
                  <div className="flex items-center gap-3 mt-1.5 text-xs text-[#7A7A72]">
                    <span className="font-medium text-[#1A3A4A] dark:text-[#F0EDE8]">{item.calories} cal</span>
                    <span className="text-[#4A90D9]">P:{item.protein}g</span>
                    <span className="text-[#E5A84B]">C:{item.carbs}g</span>
                    <span className="text-[#D4736A]">F:{item.fat}g</span>
                  </div>
                  {item.flags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {item.flags.map((f, fi) => (
                        <span key={fi} className="text-[10px] px-2 py-0.5 rounded-full bg-[#E5A84B]/10 text-[#E5A84B]">{f}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
        <div className="flex gap-3">
          <Button data-testid="rescan-menu-btn" onClick={() => { setMenuItems([]); setSelectedItems([]); }} variant="ghost" className="flex-1 h-12 rounded-xl text-[#7A7A72]">
            Rescan
          </Button>
          <Button data-testid="confirm-menu-items-btn" onClick={handleConfirmSelection} disabled={selectedItems.length === 0} className="flex-1 h-12 rounded-xl bg-[#1A3A4A] dark:bg-[#6B9E8A] text-white font-semibold disabled:opacity-40">
            Log {selectedItems.length} Item{selectedItems.length !== 1 ? 's' : ''}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in-up" data-testid="menu-scanner">
      {!imagePreview ? (
        <div onClick={() => fileInputRef.current?.click()} className="aspect-[4/3] rounded-2xl border-2 border-dashed border-[#1A3A4A]/15 dark:border-white/15 flex flex-col items-center justify-center cursor-pointer hover:border-[#1A3A4A]/30 bg-white dark:bg-[#1E2A32]">
          <div className="w-16 h-16 rounded-2xl bg-[#C67A5C]/10 flex items-center justify-center mb-4">
            <UtensilsCrossed className="w-8 h-8 text-[#C67A5C]" />
          </div>
          <p className="font-medium text-[#1A3A4A] dark:text-[#F0EDE8]">Photograph a restaurant menu</p>
          <p className="text-sm text-[#7A7A72] mt-1">AI will read and estimate nutrition</p>
        </div>
      ) : (
        <div className="relative">
          <img src={imagePreview} alt="Menu" className="w-full aspect-[3/2] object-cover rounded-2xl" />
          <button onClick={() => { setImagePreview(null); setImageFile(null); }} className="absolute top-3 right-3 w-10 h-10 rounded-full bg-black/50 text-white flex items-center justify-center">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}
      <input ref={fileInputRef} type="file" accept="image/*" capture="environment" onChange={handleFileSelect} className="hidden" />

      {error && (
        <div className="mt-4 p-4 rounded-xl bg-[#C67A5C]/10 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-[#C67A5C] flex-shrink-0 mt-0.5" />
          <p className="text-sm text-[#C67A5C]">{error}</p>
        </div>
      )}

      {scanning && (
        <div className="mt-6 flex flex-col items-center py-8">
          <Loader2 className="w-8 h-8 text-[#1A3A4A] dark:text-[#6B9E8A] animate-spin mb-3" />
          <p className="text-sm text-[#7A7A72]">Reading menu items...</p>
        </div>
      )}

      {imagePreview && !scanning && (
        <Button data-testid="scan-menu-btn" onClick={handleScan} className="w-full h-14 rounded-xl bg-[#C67A5C] hover:bg-[#d56a4f] text-white font-semibold text-base mt-4">
          <UtensilsCrossed className="w-5 h-5 mr-2" /> Scan Menu
        </Button>
      )}
    </div>
  );
};
